import { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const toDateInputValue = (value) => value.toISOString().slice(0, 10);

function defaultRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 29);
  return {
    startDate: toDateInputValue(start),
    endDate: toDateInputValue(end),
  };
}

export default function ExportDialog({
  open,
  onOpenChange,
  title,
  description,
  onSubmit,
  loading = false,
}) {
  const defaults = useMemo(() => defaultRange(), []);
  const [format, setFormat] = useState('excel');
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);

  useEffect(() => {
    if (!open) return;
    setFormat('excel');
    setStartDate(defaults.startDate);
    setEndDate(defaults.endDate);
  }, [defaults.endDate, defaults.startDate, open]);

  const rangeError = useMemo(() => {
    if (!startDate || !endDate) return 'Start date and end date are required.';
    if (startDate > endDate) return 'Start date cannot be after end date.';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dayDiff = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (dayDiff > 92) return 'Date range cannot exceed 92 days.';
    return '';
  }, [endDate, startDate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Format</Label>
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger>
                <SelectValue placeholder="Select format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excel">Excel</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            Export is limited to a maximum 92-day range to avoid heavy server load.
          </p>
          {rangeError ? (
            <p className="text-sm text-red-600">{rangeError}</p>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={loading || Boolean(rangeError)}
            onClick={() => onSubmit({ format, startDate, endDate })}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? 'Exporting...' : 'Download'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
